import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { MarketFilterModel, StoredMarketFilterModel, TemporaryArea } from "@models/map/market-filter.model";
import { ILatLng, PolyDrawService, PolygonInformationService } from "polydraw";
import { catchError, debounceTime, EMPTY, map, Observable, of, take, tap } from "rxjs";
import { SaleInArea } from "src/models/estate/IResidenceSaleInArea";
import { FilterOverview, FilterType } from "src/models/shared/filter.model";
import { AreasStore } from "src/stores/area-builder.store";
import { FavoriteListStates, OverviewFavoriteListsStore } from "src/stores/overview-favorite-lists.store";
import { SelectedFavoriteListsStore } from "src/stores/selected-favorite-list.store";
import { PolygonStorageUtil } from "src/util/map/polygon.storage.util";
import { AreaBuilderDataService } from "../area-builder-data.service";
import { InternalNavigationService } from "../internal-navigation.service";
import { MapActionsService } from "../map/map-actions.service";
import { MapMode, MapStateService } from "../map/map-state.service";
import { MarketSalesDataService } from "./market-sales-data.service";
import { MarketSalesStateService } from "./market-sales-state.service";
import { StoredFiltersDataService } from "./stored-filters-data.service";

@Injectable({
    providedIn: "root",
})
export class FavoriteListDataService {
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    readonly selectedFavoriteListStore = inject(SelectedFavoriteListsStore);
    readonly overviewFavoriteListStore = inject(OverviewFavoriteListsStore);
    private readonly mapHelperService = inject(PolyDrawService);
    private readonly storedFiltersService = inject(StoredFiltersDataService);
    private readonly mapStateService = inject(MapStateService);
    private readonly mapActionsService = inject(MapActionsService);
    private readonly marketMapStateService = inject(MarketSalesStateService);
    private readonly marketSaleDataService = inject(MarketSalesDataService);
    private readonly polygonInfoService = inject(PolygonInformationService);
    private readonly navigationService = inject(InternalNavigationService);
    readonly areasStore = inject(AreasStore);

    // Set while we programmatically redraw the map so the polygonState$ subscription ignores our own events.
    private isRedrawingMap = false;

    constructor() {
        effect(() => {
            const apiFilter = this.marketMapStateService.filterFormAPI();
            const formDiffersFromStored = this.marketMapStateService.isFavoriteListFilterDirty();
            // Sync the open list's filter with the form. Also run when filtersChanged is still set so a
            // revert to an exact form match clears it (otherwise the flag would go stale). updateFilters
            // compares criteria only, so a plain map pan does not trigger a filter change here.
            if (this.overviewFavoriteListStore.isFavoriteListOpen() && (formDiffersFromStored || this.selectedFavoriteListStore.filtersChanged())) {
                this.selectedFavoriteListStore.updateFilters(apiFilter);
            }
        });
        // Reload the overview only when a save finishes. Kept in its own effect so it does not track
        // filterFormAPI() (which changes on every map pan/zoom) and re-trigger Filter/Overview on map moves.
        effect(() => {
            if (!this.loadingFavoriteListSignal()) {
                this.favoriteListOverviewResource.reload();
            }
        });
        this.polygonInfoService.polygonState$.subscribe(() => {
            // Ignore the state events emitted by our own redraw (removeAll + re-add); the stores are already
            // in sync, and reacting to the transient "map empty" event would wipe the list's areas/polygons.
            if (this.isRedrawingMap) return;
            if (
                this.overviewFavoriteListStore.state() === FavoriteListStates.Details ||
                this.overviewFavoriteListStore.state() === FavoriteListStates.NewCart
            ) {
                this.areaBuilderService.setAreasAndPolygons(true);
                const mapData = this.areaBuilderService.getAreasAndPolygons();
                const storedPolygons = this.selectedFavoriteListStore.polygon();
                const storedAreas = this.selectedFavoriteListStore.areas();
                // Check to see if there are any new drawn polygons in map
                if (mapData.selfDrawnPolygons?.length != storedPolygons.length) {
                    this.selectedFavoriteListStore.updatePolygon(mapData.selfDrawnPolygons);
                }

                // Check to see if there are any new areas in map
                if (mapData.areas?.length != storedAreas.length) {
                    this.selectedFavoriteListStore.updateAreas(mapData.areas);
                }
            }
        });
    }

    favoriteListParams = computed(() => {
        const params = {
            mapMode: this.mapStateService.mapModeSignal(),
        };
        return params;
    });

    favoriteListOverviewResource = rxResource({
        params: () => ({
            mapMode: this.mapStateService.mapModeSignal(),
            saveFavoriteListParams: this.saveFavoriteListParams()
        }),
        stream: ({ params }) => {
            if (!params) return of([] as any);
            return this.storedFiltersService.getFilterOverview(this.getFilterTypeByMapMode(params.mapMode)).pipe(
                take(1),
                map((res) => {
                    this.overviewFavoriteListStore.load(res, this.getFilterTypeByMapMode(params.mapMode));
                }),
            );
        },
    });

    favoriteListOverviewIsLoading = this.favoriteListOverviewResource.isLoading;

    private readonly deleteFavoriteListParams = signal<number | null>(null);

    deleteFavoriteListResource = rxResource({
        params: () => this.deleteFavoriteListParams(),
        stream: ({ params }) => {
            if (params === null) return EMPTY;
            return this.storedFiltersService.deleteFilter(params).pipe(
                catchError(() => of(-1)),
                tap(() => {
                    this.selectedFavoriteListStore.clear();
                    this.overviewFavoriteListStore.removeFromOverview(params);
                    this.updateFavoriteListState(FavoriteListStates.Overview);
                    this.marketMapStateService.exitFavoriteListFilterMode();
                    this.mapStateService.updateEstateListEditMode(false);
                    this.navigationService.updateUrlParams({ favoritelist: null });
                }),
            );
        },
    });

    private readonly saveFavoriteListParams = signal<{
        filterId: number;
        filterModel: StoredMarketFilterModel;
    } | null>(null);

    saveFavoriteListResource = rxResource({
        params: () => this.saveFavoriteListParams(),
        stream: ({ params }) => {
            if (params === null) return EMPTY;
            return (
                params.filterId === -1
                    ? this.storedFiltersService.createFilter(params.filterModel)
                    : this.storedFiltersService.updateFilter(params.filterId, params.filterModel)
            ).pipe(
                debounceTime(100),
                tap((savedFilterId) => {
                    this.selectedFavoriteListStore.setId(savedFilterId.id);
                    this.selectedFavoriteListStore.updateChanged(false);
                    this.selectedFavoriteListStore.updateEditName(false);
                    this.overviewFavoriteListStore.setSelected(savedFilterId.id);
                    const sales = this.selectedFavoriteListStore.sales();
                    this.setSelectedFavoriteAndFilters(savedFilterId.id, params.filterModel);
                    this.selectedFavoriteListStore.addLazyLoadedSales(sales);
                    this.favoriteListOverviewResource.reload();
                    PolygonStorageUtil.remove();
                    this.navigationService.updateUrlParams({ areabuilderid: null });
                }),
            );
        },
    });

    loadingFavoriteListSignal = this.saveFavoriteListResource.isLoading;

    private readonly fetchUnitsParams = signal<{
        filterType: FilterType;
        unitIds: number[];
        initialLoad: boolean;
    } | null>(null);

    private readonly fetchUnitsResource = rxResource({
        params: () => this.fetchUnitsParams(),
        stream: ({ params }) => {
            if (params === null) return EMPTY;
            return this.getPropertiesForFavorite(params.filterType, params.unitIds).pipe(
                tap((sales) => {
                    if (params.initialLoad) {
                        this.addLazyLoadedSales(params.filterType, sales);
                    } else {
                        this.selectedFavoriteListStore.updateSales(sales);
                    }
                }),
            );
        },
    });

    loadingPropertiesListSignal = this.fetchUnitsResource.isLoading;

    private readonly getFavoriteListParams = signal<{
        favoriteId: number;
        rename: boolean;
        hasNewAreas: boolean;
    } | null>(null);

    favoriteListResource = rxResource({
        params: () => this.getFavoriteListParams(),
        stream: ({ params }) => {
            if (params === null) return EMPTY;
            return this.storedFiltersService.getFilter(params.favoriteId).pipe(
                tap((favorite) => {
                    // Before the list's filter overwrites the form: park the user's current working filter
                    // so Mitt filter / Basis filter is intact again once the list is closed.
                    this.marketMapStateService.enterFavoriteListFilterMode();
                    // Replace whatever is currently drawn on the map with the favorite list's polygons/areas.
                    // Skipped when hasNewAreas: those areas were just added on the map and should be kept.
                    if (!params.hasNewAreas) {
                        this.mapHelperService.removeAllFeatureGroups();
                    }
                    if (favorite.unitIds.length > 0) {
                        this.fetchUnitsForFavoriteList(favorite.filterType, favorite.unitIds);
                    }
                    this.setSelectedFavoriteAndFilters(params.favoriteId, favorite);

                    if (params.rename) {
                        this.selectedFavoriteListStore.updateEditName(true);
                    }
                    if (!params.hasNewAreas && (favorite.geoBorder.length > 0 || favorite.areas.length > 0)) {
                        this.areaBuilderService.positionMapToAreasAndPolygons(favorite.areas, favorite.geoBorder);
                    }

                    this.mapStateService.updateEstateListEditMode(true);
                    this.navigationService.updateUrlParams({ favoritelist: params.favoriteId });
                    this.overviewFavoriteListStore.setSelected(params.favoriteId);
                    this.updateFavoriteListState(FavoriteListStates.Details);
                }),
            );
        },
    });

    centerMapOnSales(salesOrDwellings: SaleInArea[], polygon: ILatLng[][][]): void {
        this.mapActionsService.gotoListMapBounds(salesOrDwellings, polygon);
    }

    removeShortSales(): void {
        this.selectedFavoriteListStore.updateSales([]);
    }

    removePolygon(polygon: ILatLng[][]): void {
        this.selectedFavoriteListStore.updatePolygon(this.selectedFavoriteListStore.polygon().filter((a) => a !== polygon));
        // Redraw from the remaining list instead of deletePolygon() so the polygon's orphaned menu marker is dropped too.
        this.redrawFavoriteListOnMap();
        // Refresh the cached map data so filterFormAPI().geoBorder (used by saveFavoriteList) reflects the removal
        this.areaBuilderService.setAreasAndPolygons(true);
    }

    removeArea(area: TemporaryArea): void {
        this.selectedFavoriteListStore.updateAreas(this.selectedFavoriteListStore.areas().filter((a) => a !== area));
        // Redraw from the remaining list instead of deletePolygon() so the area's orphaned menu marker
        // (".special-polygon-marker") is dropped along with its polygon.
        this.redrawFavoriteListOnMap();
        this.areaBuilderService.setAreasAndPolygons(true);
        const selectedArea = this.areasStore.selectedAreas().find((x) => x.areaId === area.areaId && x.areaType === (area.areaType as number));
        if (selectedArea) {
            this.areasStore.removeSelectedArea(selectedArea, selectedArea.parentAreaKey);
        }
    }

    // Wipes and redraws the open favorite list's areas and drawn polygons on the map. Guarded so the
    // polygonState$ subscription ignores the transient events this produces.
    private redrawFavoriteListOnMap(): void {
        this.isRedrawingMap = true;
        try {
            this.areaBuilderService.redrawAreasAndPolygons(this.selectedFavoriteListStore.areas(), this.selectedFavoriteListStore.polygon());
        } finally {
            this.isRedrawingMap = false;
        }
    }

    updateFavoriteListName(name: string): void {
        this.selectedFavoriteListStore.updateName(name);
        this.selectedFavoriteListStore.updateEditName(false);
    }

    getPropertiesForFavorite(filterType: FilterType, unitIds: number[]): Observable<SaleInArea[]> {
        const uniqueUnitIds = Array.from(new Set(unitIds));
        if (filterType === FilterType.Market) {
            return this.storedFiltersService.getSalesForUnits(uniqueUnitIds);
        } else if (filterType === FilterType.Properties) {
            return this.storedFiltersService.getPropertiesForUnits(uniqueUnitIds);
        } else {
            // TODO - Missing newDwelling-call here
            return of([]);
        }
    }

    closeCurrentFavoriteList(isLeaving = false): void {
        this.selectedFavoriteListStore.clear();
        this.updateFavoriteListState(FavoriteListStates.Overview);
        
        this.marketMapStateService.exitFavoriteListFilterMode();
        if (!isLeaving) {
            setTimeout(() => this.navigationService.updateUrlParams({ favoritelist: null }));
        }
        //Hvis vi ikke er i edit mode så skal vi ikke resette polygoner
        if (this.mapStateService.getEstateListEditMode()) {
            if (!isLeaving) {
                this.mapStateService.updateEstateListEditMode(false);
            }
            this.mapHelperService.closeAndReset();
        }
    }

    newFavoriteList(prefill: boolean = false) {
        // A new list keeps the filter the user is looking at right now — Mitt filter, Basis filter or
        // the filter of a list that was already open — and that filter becomes the list's Undo baseline.
        this.marketMapStateService.enterFavoriteListFilterMode();
        this.marketMapStateService.setFavoriteListBaselineFromCurrentFilter();
        this.selectedFavoriteListStore.clear();
        this.selectedFavoriteListStore.updateEditName(true);
        const mapMode = this.mapStateService.getCurrentMapMode();
        this.overviewFavoriteListStore.addToOverview({ id: -1, name: "", filterType: this.getFilterTypeByMapMode(mapMode) } as FilterOverview);
        this.overviewFavoriteListStore.setSelected(-1);
        this.updateFavoriteListState(FavoriteListStates.NewCart);
        this.mapStateService.updateEstateListEditMode(true);

        if (prefill) {
            this.addShortSales();
        }

        this.selectedFavoriteListStore.updateFilters({} as MarketFilterModel);
        // Has polygons or areas in map already
        this.areaBuilderService.setAreasAndPolygons(true);
        const mapData = this.areaBuilderService.getAreasAndPolygons();
        if (mapData.selfDrawnPolygons?.length > 0) {
            this.selectedFavoriteListStore.updatePolygon(mapData.selfDrawnPolygons);
        }
        if (mapData.areas?.length > 0) {
            this.selectedFavoriteListStore.updateAreas(mapData.areas);
        }
    }

    addShortSales(): void {
        let sales = this.marketSaleDataService.saleResource.value().sales;

        // Set same sort order as greenbelt if you are importing all sales
        const MAX_UNITS_IN_LIST = 500;
        const isNewDwelling = this.mapStateService.getCurrentMapMode() === MapMode.NewDwellings;
        const addNrItems = MAX_UNITS_IN_LIST - this.selectedFavoriteListStore.numberOfUnits();
        if (isNewDwelling) {
            // this.selectedFavoriteListStore.updateNewDwellings((res as INewDwellingInfo[]).slice(0, addNrItems));
        } else {
            this.selectedFavoriteListStore.updateSales(this.filterLongSaleList(sales as SaleInArea[]).slice(0, addNrItems));
        }
    }

    deleteFavoriteList(favoriteId: number = null): void {
        const id = favoriteId ?? this.overviewFavoriteListStore.selectedId();
        this.deleteFavoriteListParams.set(id);
    }

    saveFavoriteList() {
        if (this.selectedFavoriteListStore.canSave()) {
            const mapMode = this.mapStateService.getCurrentMapMode();
            const uniqueUnitIds = Array.from(new Set(this.selectedFavoriteListStore.units()));
            const filterModel: StoredMarketFilterModel = {
                name: this.selectedFavoriteListStore.name(),
                filterType: this.getFilterTypeByMapMode(mapMode),
                unitIds: uniqueUnitIds,
                ...this.marketMapStateService.filterFormAPI(),
                geoBorder: this.selectedFavoriteListStore.polygon(),
                areas: this.selectedFavoriteListStore.areas(),
            };

            this.saveFavoriteListParams.set({ filterId: this.selectedFavoriteListStore.id(), filterModel });
        }
    }

    addUnit(unitId: number, sale: SaleInArea): void {
        const exisitingSales = this.selectedFavoriteListStore.sales() ?? [];
        if (sale) {
            const added = [...exisitingSales, sale];
            this.selectedFavoriteListStore.updateSales(added);
        } else {
            const exisitingUnits = this.selectedFavoriteListStore.units() ?? [];
            this.selectedFavoriteListStore.updateUnits([...exisitingUnits, unitId]);
            const filterType = this.getFilterTypeByMapMode(this.mapStateService.getCurrentMapMode());
            // Not present on map, we fetch the latest sale/property for the unit
            this.fetchUnitsForFavoriteList(filterType, [...exisitingUnits, unitId], false);
        }
    }

    removeUnit(unitId: number): void {
        const exisitingSales = this.selectedFavoriteListStore.sales() ?? [];
        const removed = exisitingSales.filter((s) => s.unitId !== unitId);
        this.selectedFavoriteListStore.updateSales(removed);
    }

    loadFavoriteList(favoriteId: number, rename = false, hasNewAreas = false): void {
        this.areaBuilderService.setAreasAndPolygons(true)
        this.areasStore.removeAllSelectedAreas();
        this.getFavoriteListParams.set({ favoriteId, rename, hasNewAreas });
    }

    fetchUnitsForFavoriteList(filterType: FilterType, unitIds: number[], initialLoad = true): void {
        this.fetchUnitsParams.set({ filterType, unitIds, initialLoad });
    }

    private addLazyLoadedSales(filterType: FilterType, sales: SaleInArea[] | []): void {
        this.selectedFavoriteListStore.addLazyLoadedSales(sales as SaleInArea[]);
    }

    private filterLongSaleList(sales: SaleInArea[]): SaleInArea[] {
        // unwanted sales
        const oblList = sales.filter((item) => item.ownershipType === "Obl_leilighet");
        const coopList = sales.filter((item) => item.ownershipType === "Borettslag" && item.housingCooperativeShareId == null);

        // filter them out
        let filteredList = sales.filter((el) => !oblList.includes(el));
        filteredList = filteredList.filter((el) => !coopList.includes(el));

        return filteredList;
    }

    private getFilterTypeByMapMode(mapMode: MapMode): FilterType {
        switch (mapMode) {
            case MapMode.NewDwellings:
                return FilterType.NewDwelling;
            case MapMode.Market:
                return FilterType.Market;
            case MapMode.Properties:
                return FilterType.Properties;
            default:
                return FilterType.Market;
        }
    }

    //Todo - make a general inputfilter-model
    private setSelectedFavoriteAndFilters(favoriteId: number, favorite: StoredMarketFilterModel): void {
        switch (favorite.filterType) {
            case FilterType.NewDwelling:
                this.handleNewDwellingFilter(favoriteId, favorite);
                break;
            case FilterType.Market:
                this.handleMarketFilter(favoriteId, favorite);
                break;
            case FilterType.Properties:
                this.handlePropertiesFilter(favoriteId, favorite);
                break;
            default:
                console.error(`Unknown filter type: ${favorite.filterType}`);
        }
    }

    private handleNewDwellingFilter(favoriteId: number, favorite: StoredMarketFilterModel): void {
        // this.marketMapStateService.setFilterFromStoredModel(favorite)
        // this.filterService.setNewDwellingsFilter(favorite);
    }

    private handleMarketFilter(favoriteId: number, favorite: StoredMarketFilterModel): void {
        this.marketMapStateService.setFilterFromStoredModel(favorite);

        this.selectedFavoriteListStore.load(favoriteId, favorite, this.marketMapStateService.filterFormAPI());
    }

    private handlePropertiesFilter(favoriteId: number, favorite: StoredMarketFilterModel): void {
        // this.filterService.setPropertyFilter(favorite);
    }

    updateFavoriteListState(state: FavoriteListStates): void {
        this.overviewFavoriteListStore.updateState(state);
    }
}
